import { motion } from "framer-motion";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    title: { ru: 'Принятие условий', uk: 'Прийняття умов', en: 'Acceptance' },
    body:  { ru: 'Используя сервисы VangardClub, вы соглашаетесь с этими условиями. Если вы не согласны — пожалуйста, не используйте сервис.', uk: 'Використовуючи сервіси VangardClub, ви погоджуєтеся з цими умовами. Якщо ви не згодні — будь ласка, не використовуйте сервіс.', en: 'By accessing or using VangardClub services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.' },
  },
  {
    title: { ru: 'Членство', uk: 'Членство', en: 'Membership' },
    body:  { ru: 'Членство в VangardClub — по приглашению или заявке. Мы оставляем право принимать или отклонять заявки по своему усмотрению. Условия и стоимость могут меняться с уведомлением.', uk: 'Членство у VangardClub — за запрошенням або заявкою. Ми залишаємо за собою право приймати чи відхиляти заявки на свій розсуд. Умови та вартість можуть змінюватися з повідомленням.', en: 'VangardClub membership is by invitation or application only. We reserve the right to accept or decline any application at our sole discretion. Benefits and fees are subject to change with notice.' },
  },
  {
    title: { ru: 'Услуги', uk: 'Послуги', en: 'Services' },
    body:  { ru: 'Наши concierge-сервисы предоставляются на best-efforts основе. Некоторые услуги зависят от доступности, условий третьих сторон и факторов вне нашего контроля.', uk: 'Наші concierge-сервіси надаються на best-efforts основі. Деякі послуги залежать від доступності, умов третіх сторін та факторів поза нашим контролем.', en: 'Our concierge services are provided on a best-efforts basis. Certain services may be subject to availability, third-party terms and other factors beyond our control.' },
  },
  {
    title: { ru: 'Поведение', uk: 'Поведінка', en: 'Conduct' },
    body:  { ru: 'Вы соглашаетесь не использовать сервисы в незаконных целях, не делиться своими учётными данными, не искажать личность, не мешать работе сервиса и не наносить вред клубу или его участникам.', uk: 'Ви погоджуєтеся не використовувати сервіси у незаконних цілях, не ділитися своїми обліковими даними, не спотворювати особу, не заважати роботі сервісу та не завдавати шкоди клубу.', en: 'You agree not to use our services for any unlawful purpose, share your membership credentials, misrepresent your identity, interfere with the proper functioning of our services, or engage in activity that could harm the club or its members.' },
  },
  {
    title: { ru: 'Оплата', uk: 'Оплата', en: 'Payment' },
    body:  { ru: 'Членские взносы и плата за услуги оплачиваются согласно вашему соглашению о членстве. Все платежи невозвратные, если не указано иное.', uk: 'Членські внески та плата за послуги оплачуються згідно з вашою угодою про членство. Усі платежі неповернувані, якщо не зазначено інше.', en: 'Membership fees and service charges are billed as specified in your membership agreement. All payments are non-refundable unless otherwise stated.' },
  },
  {
    title: { ru: 'Ответственность', uk: 'Відповідальність', en: 'Liability' },
    body:  { ru: 'VangardClub не несёт ответственности за косвенные, случайные, специальные или штрафные убытки. Общая ответственность не превышает суммы взносов за 12 месяцев.', uk: 'VangardClub не несе відповідальності за непрямі, випадкові, спеціальні чи штрафні збитки. Загальна відповідальність не перевищує суми внесків за 12 місяців.', en: 'VangardClub shall not be liable for indirect, incidental, special, consequential or punitive damages arising from your use of our services. Our total liability shall not exceed the fees paid by you in the preceding 12 months.' },
  },
  {
    title: { ru: 'Прекращение', uk: 'Припинення', en: 'Termination' },
    body:  { ru: 'Любая сторона может прекратить членство с письменным уведомлением. Мы можем приостановить или прекратить членство немедленно за нарушение условий.', uk: 'Будь-яка сторона може припинити членство з письмовим повідомленням. Ми можемо призупинити або припинити членство негайно за порушення умов.', en: 'Either party may terminate membership with written notice. We reserve the right to suspend or terminate membership immediately for violation of these terms or conduct detrimental to the club.' },
  },
  {
    title: { ru: 'Контакт', uk: 'Контакт', en: 'Contact' },
    body:  { ru: 'По вопросам этих условий: legal@vangardclub.com.', uk: 'Із питань цих умов: legal@vangardclub.com.', en: 'For questions about these terms, contact us at legal@vangardclub.com.' },
  },
];

export default function TermsPage() {
  const { lang, t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="terms-page">
      <Seo title={t("vc.seo.terms.title")} description={t("vc.seo.terms.desc")} />
      <VangardHeader />

      <section className="relative overflow-hidden pt-[92px]">
        <VMark position="top-left" size={620} opacity={0.05} />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.legal.eyebrow")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-6 leading-[1.05]">
              {t("vc.legal.terms-title")} <span className="italic text-neutral-300">{t("vc.legal.terms-italic")}</span>
            </h1>
            <p className="mt-6 text-sm text-neutral-500 tracking-wide">{t("vc.legal.updated")}</p>
          </motion.div>

          <div className="mt-16 space-y-12">
            {SECTIONS.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.03 * i }}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-neutral-600 text-xl w-8">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-2xl">{s.title[lang]}</h2>
                </div>
                <p className="mt-4 pl-12 text-neutral-400 font-light leading-relaxed">{s.body[lang]}</p>
              </motion.section>
            ))}
          </div>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
